import React, { useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import useMeasure from "react-use-measure";

/**
 * Clean, SCSS-driven Accordion
 * - All styling is handled in SCSS via BEM-style classes.
 * - Accessible: uses aria-expanded, aria-controls, role="region".
 * - Props:
 *   - title: string (header text)
 *   - defaultOpen: boolean (initial open state)
 *   - disabled: boolean (disables toggle)
 */
export const Accordion = ({
  title = "Lorem ipsum dolor sit amet",
  defaultOpen = false,
  disabled = false,
  body = "",
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [ref, { height: viewHeight }] = useMeasure();

  const slugify = (s) =>
    String(s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

  const panelId = `accordion-panel-${slugify(title)}`;
  const headerId = `accordion-header-${slugify(title)}`;

  const onToggle = () => {
    if (disabled) return;
    setIsOpen((v) => !v);
  };

  const styles = useSpring({
    from: { height: 0, opacity: 0 },
    to: {
      height: isOpen ? viewHeight : 0,
      opacity: isOpen ? 1 : 0,
    },
    config: { tension: 250, friction: 30 },
  });

  return (
    <div
      className={["accordion","grid content-start", isOpen ? "is-open" : "", disabled ? "is-disabled" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className="accordion__header"
        id={headerId}
        aria-controls={panelId}
        aria-expanded={isOpen}
        onClick={onToggle}
        disabled={disabled}
      >
        <span className="accordion__title t-h4">{title}</span>
        <span className={["material-icons", "accordion__icon", isOpen ? "is-open" : ""].join(" ") }>
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </div>

      <animated.div
        className="accordion__panel"
      >
        <div ref={ref} className="accordion__body">
          {children ? (
            children
          ) : (
            <p className="accordion__text t-body">{body}</p>
          )}
        </div>
      </animated.div>
    </div>
  );
};

export default Accordion;
